import * as React from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { faCalendar, IconDefinition } from '@fortawesome/pro-regular-svg-icons'
import { Button, Flex } from 'antd'
import dayjs, { Dayjs } from 'dayjs'
import isNil from 'lodash.isnil'

import { useOrganizationHolidaysWithinDateRangeQuery } from 'src/fetch/organizationHolidays'
import useDisclosure from 'src/hooks/use-disclosure'

import { TimeZone } from '../../../../types/graphql'
import Text from '../../../components/Typography/Text'
import { KeyboardKey } from '../../../types'
import {
  convertDateToTimeZone,
  DateFormats,
  getDayjsInTimeZone,
  getLocalizedDateFromSemanticDate,
  getSemanticDate,
} from '../../../utils/date'
import { formatNumber, NumberFormat } from '../../../utils/number'
import { Size } from '../../types'
import Calendar from '../Calendar/Calendar'
import HStack from '../Flex/HStack'
import { FormControlProps } from '../FormControl/util'
import Icon from '../Icon/Icon'
import Input from '../Input/Input'
import NumberInput from '../Input/NumberInput'
import Popover from '../Popover/Popover'
import Tooltip from '../Tooltip/Tooltip'

import {
  CalendarHeaderLabelButton,
  CalendarHeaderNavButton,
  DatePickerPopoverContent,
  QuickPickCell,
} from './DatePicker.styles'
import type { QuickPickCellState } from './DatePicker.styles'
import { formatDateForInputText, getIsStringCompleteDate, MONTH_LABELS } from './util'

const YEAR_GRID_SIZE = 12

// Not `as const` — rdp's DayOfWeek matcher expects a mutable `number[]`.
const weekendModifier = { weekend: { dayOfWeek: [0, 6] } }

// rdp only auto-emits CSS classes for built-in modifiers; custom ones
// like "weekend" need an explicit `modifiersClassNames` mapping.
const weekendModifierClassNames = { weekend: 'rdp-weekend' }

type CalendarViewMode = 'days' | 'months' | 'years'

export enum DatePickerAllowedDates {
  Future = 'future',
  All = 'all',
}

type Props = Omit<FormControlProps, 'isLabelRedundant'> & {
  format?: DateFormats
  getIsDateDisabled?: (date: Dayjs) => boolean
  isClearable?: boolean
  isDisabled?: boolean
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void
  onValueChange: (value: Dayjs | null) => void
  organizationId?: number
  placeholder?: string
  shouldExcludeHolidays?: boolean
  size?: Size
  suffixIcon?: IconDefinition | null
  textAlign?: Extract<React.CSSProperties['textAlign'], 'left' | 'center'>
  timeZone?: TimeZone | null
  value: Dayjs | null | undefined
}
const DatePicker = ({
  ariaLabel,
  caption,
  errorMessage,
  format,
  getIsDateDisabled: propGetIsDateDisabled,
  id,
  isClearable,
  isDisabled,
  isInvalid,
  isRequired,
  label,
  onBlur,
  onValueChange,
  organizationId,
  placeholder,
  shouldExcludeHolidays = false,
  hideRequiredAsterisk,
  size,
  suffixIcon = faCalendar,
  textAlign = 'left',
  timeZone,
  tooltip,
  value,
  width,
}: Props) => {
  // Fetch holidays for next 60 days
  const holidayEndDate = useMemo(() => dayjs().add(60, 'day').toISOString(), [])
  const { holidays } = useOrganizationHolidaysWithinDateRangeQuery({
    organizationId,
    endDate: holidayEndDate,
  })

  const holidayDates = useMemo(
    () =>
      holidays
        .map((h) => getLocalizedDateFromSemanticDate(h.date))
        .filter((d): d is Dayjs => d !== null),
    [holidays]
  )

  const popoverState = useDisclosure()
  const popoverContentRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const weekdaysButtonRef = useRef<HTMLButtonElement>(null)
  const weekdaysInputRef = useRef<HTMLInputElement>(null)

  const {
    isOpen: isEditingWeekdays,
    open: startEditingWeekdays,
    close: stopEditingWeekdays,
  } = useDisclosure()

  const handleEditWeekdaysClicked = useCallback(() => {
    startEditingWeekdays()
    requestAnimationFrame(() => {
      weekdaysInputRef.current?.focus()
    })
  }, [startEditingWeekdays])

  const handleClose = useCallback(() => {
    popoverState.close()
    stopEditingWeekdays()
  }, [popoverState, stopEditingWeekdays])

  const numberOfWeekdays = useMemo(() => {
    const nowInTimeZone = getDayjsInTimeZone(dayjs(), timeZone).startOf('day')
    return value ? dayjs(value).startOf('day').businessDiff(nowInTimeZone) : undefined
  }, [value, timeZone])
  const [inputNumberOfWeekdays, setInputNumberOfWeekdays] = useState(numberOfWeekdays)
  const maxWeekdays = 1000
  const minWeekdays = -1000
  const isNumberOfWeekdaysOutOfRange =
    !isNil(inputNumberOfWeekdays) &&
    (inputNumberOfWeekdays > maxWeekdays || inputNumberOfWeekdays < minWeekdays)

  const defaultInputText = useMemo(
    () =>
      formatDateForInputText(value, {
        format,
      }),
    [format, value]
  )
  const [inputText, setInputText] = useState(defaultInputText) // The user's temporary text entry
  const defaultMonth = useMemo(() => value ?? dayjs(), [value])
  const [month, setMonth] = useState(defaultMonth) // The month which is being viewed in the popover calendar
  // Local view state for the in-popover quick pickers. Always reset to 'days'
  // when the popover (re)opens so the user starts on the familiar day grid.
  const [viewMode, setViewMode] = useState<CalendarViewMode>('days')

  // Create a local Date with the same year/month/day as the value
  // This is necessary because the value may have been timezone-adjusted, which shifts the UTC timestamp.
  // If we just called value.toDate(), react-day-picker would interpret that Date in local timezone,
  // potentially highlighting the wrong day.
  const selectedDate = useMemo(() => {
    return getSemanticDate(value) ?? undefined
  }, [value])
  const selectedMonth = useMemo(() => month.toDate(), [month])

  const { isOpen: hasFocus, open: focus, close: blur } = useDisclosure()

  useEffect(() => {
    if (!hasFocus) {
      // If the prop value changes while the input does not have focus, reset the input
      setInputText(defaultInputText)
    }
  }, [defaultInputText, hasFocus])

  const getIsDateDisabled = useCallback(
    (date: Date) => {
      // Check holidays
      if (shouldExcludeHolidays && holidayDates.some((d) => d.isSame(dayjs(date), 'day'))) {
        return true
      }
      return propGetIsDateDisabled?.(dayjs(date)) ?? false
    },
    [propGetIsDateDisabled, shouldExcludeHolidays, holidayDates]
  )

  const getDateTooltip = useCallback(
    (date: Date) => {
      const holiday = holidays.find((h) => {
        const holidayDate = getLocalizedDateFromSemanticDate(h.date)
        return holidayDate?.isSame(dayjs(date), 'day')
      })
      return holiday?.name
    },
    [holidays]
  )

  const handleValueChange = useCallback(
    (newValue: Dayjs | null, resetInputText: boolean = true) => {
      if (newValue && getIsDateDisabled(newValue.toDate())) {
        return
      }
      onValueChange(convertDateToTimeZone(newValue, timeZone))
      if (newValue) {
        setMonth(newValue)
      }
      if (resetInputText) {
        setInputText(formatDateForInputText(newValue, { format }))
      }
    },
    [format, getIsDateDisabled, onValueChange, timeZone]
  )

  const handleInputTextChanged = useCallback(
    (newValue: string | undefined) => {
      if (!newValue) {
        setInputText('')
        if (isClearable) {
          handleValueChange(null)
        }
        return
      }

      setInputText(newValue)

      if (getIsStringCompleteDate(newValue)) {
        // don't reset the input text as the user is still typing
        handleValueChange(dayjs(newValue), false)
      }
    },
    [handleValueChange, isClearable]
  )

  const handleOpen = useCallback(() => {
    if (!popoverState.isOpen) {
      setMonth(defaultMonth)
      setViewMode('days')
      popoverState.open()
    }
  }, [defaultMonth, popoverState])

  const handleInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === KeyboardKey.ArrowDown) {
        e.preventDefault()
        handleOpen()
        requestAnimationFrame(() => {
          popoverContentRef.current?.focus()
        })
      }
    },
    [handleOpen]
  )

  const handleInputClicked = useCallback(() => {
    handleOpen()
  }, [handleOpen])

  const handleInputFocused = useCallback(() => {
    focus()
    handleOpen()
  }, [focus, handleOpen])

  const handleInputBlurred = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      if (getIsStringCompleteDate(inputText)) {
        handleValueChange(dayjs(inputText))
      } else {
        setInputText(defaultInputText)
      }

      blur()
      setTimeout(() => {
        // Timeout needed to skip render cycle for activeElement to update (where focus is)
        if (!popoverContentRef.current?.contains(document.activeElement)) {
          // Don't close the popover if it gained focus
          handleClose()
          onBlur?.(e)
        }
      })
    },
    [blur, defaultInputText, handleClose, handleValueChange, inputText, onBlur]
  )

  const handleInteractOutsidePopover = useCallback(
    (
      e: Parameters<
        NonNullable<React.ComponentProps<typeof Popover.Content>['onInteractOutside']>
      >[0]
    ) => {
      if (hasFocus) {
        e.preventDefault()
      }
    },
    [hasFocus]
  )

  const handleMonthChanged = useCallback((newValue: Date | undefined) => {
    setMonth(newValue ? dayjs(newValue) : dayjs())
  }, [])

  const handleCalendarDateSelected = useCallback(
    (newValue: Date | undefined) => {
      if (!newValue) {
        return
      }
      const newDateValue = dayjs(newValue)
      handleValueChange(newDateValue)
      handleClose()
      inputRef.current?.focus()
    },
    [handleClose, handleValueChange]
  )

  const handleTodayClicked = useCallback(() => {
    if (getIsDateDisabled(new Date())) {
      return
    }
    handleCalendarDateSelected(new Date())
  }, [getIsDateDisabled, handleCalendarDateSelected])

  // Drives the disabled state of the "Set to today" button. Disabling
  // (rather than hiding) the button keeps the popover at a fixed height
  // when the user toggles between today and another date.
  const isValueToday = useMemo(() => {
    if (!value) {
      return false
    }
    const nowInTimeZone = getDayjsInTimeZone(dayjs(), timeZone)
    return dayjs(value).isSame(nowInTimeZone, 'day')
  }, [value, timeZone])

  const startMonth = useMemo(() => dayjs().subtract(100, 'year').toDate(), [])
  const endMonth = useMemo(() => dayjs().add(20, 'year').toDate(), [])

  const handleShowMonthGrid = useCallback(() => setViewMode('months'), [])
  const handleShowYearGrid = useCallback(() => setViewMode('years'), [])

  const handleMonthCellClicked = useCallback((monthIndex: number) => {
    setMonth((prev) => prev.month(monthIndex))
    setViewMode('days')
  }, [])

  const handleYearCellClicked = useCallback((year: number) => {
    setMonth((prev) => prev.year(year))
    // Surface the months grid next so the user can finish drilling in.
    setViewMode('months')
  }, [])

  // The first year of the 12-year window currently shown by the year grid.
  // Decade-aligned (e.g. 2024-2035, 2036-2047) so successive [<]/[>] clicks
  // always land on the same window endpoints — easier to scan than a sliding
  // window. Mirrors the pattern in Google Calendar / Apple's date picker.
  const yearWindowStart = useMemo(
    () => Math.floor(month.year() / YEAR_GRID_SIZE) * YEAR_GRID_SIZE,
    [month]
  )

  const startMonthYear = useMemo(() => dayjs(startMonth).year(), [startMonth])
  const endMonthYear = useMemo(() => dayjs(endMonth).year(), [endMonth])
  const isPrevDisabled = useMemo(() => {
    if (viewMode === 'days') {
      return month.isSame(dayjs(startMonth), 'month') || month.isBefore(dayjs(startMonth), 'month')
    }
    if (viewMode === 'months') {
      return month.year() <= startMonthYear
    }
    return yearWindowStart <= startMonthYear
  }, [month, startMonth, startMonthYear, viewMode, yearWindowStart])
  const isNextDisabled = useMemo(() => {
    if (viewMode === 'days') {
      return month.isSame(dayjs(endMonth), 'month') || month.isAfter(dayjs(endMonth), 'month')
    }
    if (viewMode === 'months') {
      return month.year() >= endMonthYear
    }
    return yearWindowStart + YEAR_GRID_SIZE > endMonthYear
  }, [endMonth, endMonthYear, month, viewMode, yearWindowStart])

  const handleNavPrev = useCallback(() => {
    if (viewMode === 'days') {
      setMonth((m) => m.subtract(1, 'month'))
    } else if (viewMode === 'months') {
      setMonth((m) => m.subtract(1, 'year'))
    } else {
      setMonth((m) => m.subtract(YEAR_GRID_SIZE, 'year'))
    }
  }, [viewMode])

  const handleNavNext = useCallback(() => {
    if (viewMode === 'days') {
      setMonth((m) => m.add(1, 'month'))
    } else if (viewMode === 'months') {
      setMonth((m) => m.add(1, 'year'))
    } else {
      setMonth((m) => m.add(YEAR_GRID_SIZE, 'year'))
    }
  }, [viewMode])

  // Year-step nav (the outer double-chevrons in days view). Hidden in
  // months/years views, since the singles in those views already step a
  // year/decade — adding more arrows would just duplicate behavior.
  const handleNavPrevYear = useCallback(() => {
    setMonth((m) => m.subtract(1, 'year'))
  }, [])

  const handleNavNextYear = useCallback(() => {
    setMonth((m) => m.add(1, 'year'))
  }, [])

  const isPrevYearDisabled = useMemo(() => month.year() <= startMonthYear, [month, startMonthYear])
  const isNextYearDisabled = useMemo(() => month.year() >= endMonthYear, [month, endMonthYear])

  const monthGridSelectedIndex = useMemo(() => month.month(), [month])
  const monthGridTodayIndex = useMemo(() => {
    const now = dayjs()
    return now.year() === month.year() ? now.month() : -1
  }, [month])

  const yearGridSelectedYear = useMemo(() => month.year(), [month])
  const todayYear = useMemo(() => dayjs().year(), [])

  // "ago" / "from now" mirror the language in the date-range direction
  // picker (ReportDatePeriodSection), so the two pickers feel like one
  // shared vocabulary.
  const weekdaysLabel = useMemo(() => {
    if (isNil(inputNumberOfWeekdays)) {
      return ' weekdays from now'
    }
    if (inputNumberOfWeekdays === 0) {
      return ' (today)'
    }
    if (inputNumberOfWeekdays > 0) {
      return ` ${inputNumberOfWeekdays === 1 ? 'weekday' : 'weekdays'} from now`
    }
    return ` ${inputNumberOfWeekdays === -1 ? 'weekday' : 'weekdays'} ago`
  }, [inputNumberOfWeekdays])

  const weekdaysButtonLabel = useMemo(() => {
    if (isNil(inputNumberOfWeekdays)) {
      return 'Pick by # of weekdays from today'
    }
    // "Selected: today" reads more naturally than "Selected: 0 weekdays
    // from now" when the user has the current day selected.
    if (inputNumberOfWeekdays === 0) {
      return 'Selected: today'
    }
    // "Selected:" prefix turns the row from a mystery action into a clear
    // status line: "this is your current selection, expressed as N weekdays
    // from today — click to edit."
    return `Selected: ${formatNumber(Math.abs(inputNumberOfWeekdays), {
      format: NumberFormat.Integer,
    })}${weekdaysLabel}`
  }, [inputNumberOfWeekdays, weekdaysLabel])

  useEffect(() => {
    setInputNumberOfWeekdays(numberOfWeekdays)
  }, [numberOfWeekdays])
  const handleInputNumberOfWeekdaysChange = useCallback(
    (newNumberOfWeekdays: number | undefined) => {
      setInputNumberOfWeekdays(newNumberOfWeekdays)
      if (isNil(newNumberOfWeekdays)) {
        // Don't clear out the date value
        return
      }
      const today = dayjs().startOf('day')
      const newValue = today.businessDaysAdd(newNumberOfWeekdays)
      handleValueChange(newValue)
    },
    [handleValueChange]
  )

  const saveWeekdaysInput = useCallback(() => {
    stopEditingWeekdays()
    setInputNumberOfWeekdays(numberOfWeekdays)
  }, [numberOfWeekdays, stopEditingWeekdays])

  const handleNumberOfWeekdaysInputBlur = useCallback(() => {
    saveWeekdaysInput()
  }, [saveWeekdaysInput])

  const handleNumberOfWeekdaysInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === KeyboardKey.Enter || e.key === KeyboardKey.Escape) {
        e.preventDefault()
        saveWeekdaysInput()
        requestAnimationFrame(() => {
          weekdaysButtonRef.current?.focus()
        })
      }
    },
    [saveWeekdaysInput]
  )

  const handlePopoverEscapeKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (weekdaysInputRef.current === document.activeElement) {
        e.preventDefault()
        saveWeekdaysInput()
        requestAnimationFrame(() => {
          weekdaysButtonRef.current?.focus()
        })
        return
      }
      inputRef.current?.focus()
    },
    [saveWeekdaysInput]
  )

  const leftAlignOffset = -12 // nice value for left-alignment of the input box
  const [alignOffset, setAlignOffset] = useState(leftAlignOffset)
  useEffect(() => {
    if (textAlign === 'left' || !inputRef.current) {
      setAlignOffset(leftAlignOffset)
      return
    }

    // Compute left-alignment with the input /text/ which is center-aligned
    const span = createTextWidthSpan(inputRef.current)

    const inputTextWidth = span.clientWidth
    const inputWidth = inputRef.current.clientWidth
    setAlignOffset(Math.max((inputWidth - inputTextWidth) / 2 - 16, leftAlignOffset))
  }, [textAlign, value, inputText, leftAlignOffset])

  return (
    <Popover isOpen={popoverState.isOpen} onOpen={handleOpen} onClose={handleClose}>
      <Popover.Anchor asChild>
        <Input
          {...(suffixIcon ? { suffixIcon } : {})}
          {...(label ? { label } : { ariaLabel: ariaLabel || '' })}
          caption={caption}
          errorMessage={errorMessage}
          id={id}
          isClearable={isClearable}
          isDisabled={isDisabled}
          isInvalid={isInvalid}
          isRequired={isRequired}
          hideRequiredAsterisk={hideRequiredAsterisk}
          tooltip={tooltip}
          onBlur={handleInputBlurred}
          onClick={handleInputClicked}
          onFocus={handleInputFocused}
          onKeyDown={handleInputKeyDown}
          onValueChange={handleInputTextChanged}
          placeholder={placeholder ?? 'Select a date'}
          ref={inputRef}
          size={size}
          textAlign={textAlign}
          value={inputText}
          width={width}
        />
      </Popover.Anchor>
      <DatePickerPopoverContent
        align="start"
        alignOffset={alignOffset}
        autoFocus={false}
        sideOffset={size === 'sm' ? 6 : size === 'md' ? 8 : 12}
        onEscapeKeyDown={handlePopoverEscapeKeyDown}
        onFocusOutside={(e) => e.preventDefault()}
        onInteractOutside={handleInteractOutsidePopover}
        onOpenAutoFocus={(e) => e.preventDefault()}
        ref={popoverContentRef}
      >
        <HStack height="32px" justify="space-between" margin="4px 0 0 0" padding="0 8px">
          <HStack gap={1} width="auto">
            {viewMode === 'days' && (
              <CalendarHeaderNavButton
                type="button"
                aria-label="Previous year"
                disabled={isPrevYearDisabled}
                onClick={handleNavPrevYear}
              >
                <Icon.FaAnglesLeft />
              </CalendarHeaderNavButton>
            )}
            <CalendarHeaderNavButton
              type="button"
              aria-label={
                viewMode === 'days'
                  ? 'Previous month'
                  : viewMode === 'months'
                    ? 'Previous year'
                    : 'Previous decade'
              }
              disabled={isPrevDisabled}
              onClick={handleNavPrev}
            >
              <Icon.FaChevronLeft />
            </CalendarHeaderNavButton>
          </HStack>
          <HStack gap={1} width="auto">
            {viewMode === 'days' && (
              <>
                <CalendarHeaderLabelButton
                  type="button"
                  aria-label={`Pick month — currently ${month.format('MMMM')}`}
                  onClick={handleShowMonthGrid}
                >
                  {month.format('MMM')}
                </CalendarHeaderLabelButton>
                <CalendarHeaderLabelButton
                  type="button"
                  aria-label={`Pick year — currently ${month.format('YYYY')}`}
                  onClick={handleShowYearGrid}
                >
                  {month.format('YYYY')}
                </CalendarHeaderLabelButton>
              </>
            )}
            {viewMode === 'months' && (
              <CalendarHeaderLabelButton
                type="button"
                aria-label={`Pick year — currently ${month.format('YYYY')}`}
                onClick={handleShowYearGrid}
              >
                {month.format('YYYY')}
              </CalendarHeaderLabelButton>
            )}
            {viewMode === 'years' && (
              <CalendarHeaderLabelButton type="button" disabled>
                {`${yearWindowStart} – ${yearWindowStart + YEAR_GRID_SIZE - 1}`}
              </CalendarHeaderLabelButton>
            )}
          </HStack>
          <HStack gap={1} width="auto">
            <CalendarHeaderNavButton
              type="button"
              aria-label={
                viewMode === 'days'
                  ? 'Next month'
                  : viewMode === 'months'
                    ? 'Next year'
                    : 'Next decade'
              }
              disabled={isNextDisabled}
              onClick={handleNavNext}
            >
              <Icon.FaChevronRight />
            </CalendarHeaderNavButton>
            {viewMode === 'days' && (
              <CalendarHeaderNavButton
                type="button"
                aria-label="Next year"
                disabled={isNextYearDisabled}
                onClick={handleNavNextYear}
              >
                <Icon.FaAnglesRight />
              </CalendarHeaderNavButton>
            )}
          </HStack>
        </HStack>
        <HStack height="232px" justify="center">
          {viewMode === 'days' && (
            <Calendar
              captionLayout="label"
              className="gw-datepicker-calendar"
              disabled={getIsDateDisabled}
              startMonth={startMonth}
              endMonth={endMonth}
              fixedWeeks
              getDateTooltip={getDateTooltip}
              mode="single"
              modifiers={weekendModifier}
              modifiersClassNames={weekendModifierClassNames}
              month={selectedMonth}
              onMonthChange={handleMonthChanged}
              onSelect={handleCalendarDateSelected}
              selected={selectedDate}
            />
          )}
          {viewMode === 'months' && (
            <div
              className="grid w-[240px] grid-cols-4 gap-1 p-2"
              role="grid"
              aria-label="Pick a month"
            >
              {MONTH_LABELS.map((monthLabel, index) => {
                const state: QuickPickCellState =
                  index === monthGridSelectedIndex
                    ? 'selected'
                    : index === monthGridTodayIndex
                      ? 'today'
                      : 'default'
                return (
                  <QuickPickCell
                    key={monthLabel}
                    type="button"
                    $state={state}
                    aria-label={`${dayjs().month(index).format('MMMM')} ${month.year()}`}
                    aria-current={state === 'selected' ? 'true' : undefined}
                    onClick={() => handleMonthCellClicked(index)}
                  >
                    {monthLabel}
                  </QuickPickCell>
                )
              })}
            </div>
          )}
          {viewMode === 'years' && (
            <div
              className="grid w-[240px] grid-cols-4 gap-1 p-2"
              role="grid"
              aria-label="Pick a year"
            >
              {Array.from({ length: YEAR_GRID_SIZE }).map((_, i) => {
                const year = yearWindowStart + i
                const isOutOfRange = year > endMonthYear
                const state: QuickPickCellState = isOutOfRange
                  ? 'default'
                  : year === yearGridSelectedYear
                    ? 'selected'
                    : year === todayYear
                      ? 'today'
                      : 'default'
                return (
                  <QuickPickCell
                    key={year}
                    type="button"
                    $state={state}
                    disabled={isOutOfRange}
                    aria-label={String(year)}
                    aria-current={state === 'selected' ? 'true' : undefined}
                    onClick={() => handleYearCellClicked(year)}
                  >
                    {year}
                  </QuickPickCell>
                )
              })}
            </div>
          )}
        </HStack>
        <Flex vertical align="center" justify="center">
          <Button
            type="link"
            disabled={isValueToday}
            onClick={handleTodayClicked}
            icon={<Icon.FaCalendarDay />}
          >
            Set to today
          </Button>
          {isEditingWeekdays ? (
            <Flex align="center" gap="8px">
              <NumberInput
                ariaLabel="Number of weekdays"
                allowNegative
                format={NumberFormat.Integer}
                leftAddon={null}
                max={1000}
                min={-1000}
                onBlur={handleNumberOfWeekdaysInputBlur}
                onKeyDown={handleNumberOfWeekdaysInputKeyDown}
                onValueChange={handleInputNumberOfWeekdaysChange}
                ref={weekdaysInputRef}
                textAlign="right"
                value={inputNumberOfWeekdays}
                width="64px"
              />
              <Text>{weekdaysLabel}</Text>
            </Flex>
          ) : (
            <Tooltip
              content={
                <>
                  Select a date that is a specific number of weekdays in the <em>past</em> (-) or
                  the <em>future</em> (+) relative to today.
                </>
              }
            >
              <Button
                disabled={isNumberOfWeekdaysOutOfRange}
                icon={<Icon.FaPenToSquare />}
                onClick={handleEditWeekdaysClicked}
                ref={weekdaysButtonRef}
                type="link"
              >
                {weekdaysButtonLabel}
              </Button>
            </Tooltip>
          )}
        </Flex>
      </DatePickerPopoverContent>
    </Popover>
  )
}

export default React.memo(DatePicker)

/**
 * @description Estimates the width of the internal input value text with a hidden span. The input value is rendered in the shadow DOM and is otherwise inaccessible.
 */
const createTextWidthSpan = (input: HTMLInputElement) => {
  const computedStyles = window.getComputedStyle(input)

  // Create a span in memory
  const span = document.createElement('span')

  // Copy only relevant text styles
  const styleProps: Extract<
    keyof HTMLInputElement['style'],
    | 'font'
    | 'fontSize'
    | 'fontFamily'
    | 'fontWeight'
    | 'fontStyle'
    | 'letterSpacing'
    | 'textTransform'
    | 'textIndent'
    | 'textDecoration'
    | 'whiteSpace'
    | 'wordSpacing'
    | 'lineHeight'
  >[] = [
    'font',
    'fontSize',
    'fontFamily',
    'fontWeight',
    'fontStyle',
    'letterSpacing',
    'textTransform',
    'textIndent',
    'textDecoration',
    'whiteSpace',
    'wordSpacing',
    'lineHeight',
  ]

  styleProps.forEach((prop) => {
    span.style[prop] = computedStyles[prop]
  })

  // Set the text content to the input's value
  span.textContent = input.value || input.placeholder || ''

  // Avoid line breaks and make sure it's hidden from view
  span.style.position = 'absolute'
  span.style.visibility = 'hidden'
  span.style.whiteSpace = 'pre'

  // Append to body for layout measuring
  document.body.appendChild(span)

  return span
}
